/**
 * AppointmentOutcomePanel — Phase 1 read-only view
 * ────────────────────────────────────────────────
 * Five-section structured view that tells the full story of an
 * appointment in one scannable surface:
 *
 *   1. Outcome Summary
 *   2. Proposal Snapshot
 *   3. Objections
 *   4. Next Steps / Actions
 *   5. Activity (linked follow-ups)
 *
 * Wired into both /appointment-outcome/:id (dedicated route) and as a
 * compact link-out from JobDetail + Sales Command Center.
 *
 * NO inline `if (resultType === ...)` branches — derivations live in
 * src/data/situational/outcomeCapture.ts.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle2, XCircle, RotateCcw, AlertTriangle,
  CalendarClock, User, MapPin, Tag, FileText,
  Wallet, Receipt, ShieldAlert, ClipboardList, Wrench,
} from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import type { AppointmentResult } from "@/data/appointmentResultTypes";
import { RESULT_TYPE_CONFIG } from "@/data/appointmentResultTypes";
import {
  OBJECTION_LABELS,
  FOLLOW_UP_TYPE_LABELS,
  COMMUNICATION_STATUS_LABELS,
  CLOSE_REASON_LABELS,
} from "@/data/appointmentOutcomeTypes";
import {
  lifecycleToneClasses,
  commsStatusToneClasses,
} from "@/data/situational/outcomeCapture";
import { resolveProfileDisplayName } from "@/lib/profileDisplayName";

interface AppointmentOutcomePanelProps {
  appointmentResultId: string;
  /** Compact mode renders only Summary + Next Steps (for embedding). */
  compact?: boolean;
}

const RESULT_ICON: Record<AppointmentResult["resultType"], React.ComponentType<{ className?: string }>> = {
  sold: CheckCircle2,
  no_sale: XCircle,
  rescheduled: RotateCcw,
  no_demo: AlertTriangle,
};

function formatCurrency(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function formatDateTime(iso: string): string {
  try { return format(parseISO(iso), "MMM d, yyyy · h:mm a"); }
  catch { return iso; }
}

function formatRelative(iso: string): string {
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }); }
  catch { return iso; }
}

export function AppointmentOutcomePanel({ appointmentResultId, compact }: AppointmentOutcomePanelProps) {
  const { appointmentResults, appointmentOutcomeExtensions, followUpTasks, salesOpportunities } = useAppData();

  const result = useMemo(
    () => appointmentResults.find((r) => r.id === appointmentResultId),
    [appointmentResults, appointmentResultId],
  );
  const ext = useMemo(
    () => appointmentOutcomeExtensions.find((e) => e.appointmentResultId === appointmentResultId),
    [appointmentOutcomeExtensions, appointmentResultId],
  );
  const opportunity = useMemo(
    () => result ? salesOpportunities.find((o) => o.id === result.opportunityId) : undefined,
    [salesOpportunities, result],
  );
  const linkedFollowUps = useMemo(
    () => result ? followUpTasks.filter((t) => t.opportunityId === result.opportunityId) : [],
    [followUpTasks, result],
  );

  // Derive an "Actual Build" summary from the opportunity's projectConfig.
  // This is what the rep configured in Step 6 (Design Configuration) — distinct
  // from the Proposal Snapshot (commercial layer). Both should be visible.
  const buildRows = useMemo(() => {
    const cfg = opportunity?.projectConfig;
    if (!cfg) return [] as Array<{ label: string; value: string }>;
    const rows: Array<{ label: string; value: string }> = [];
    if (cfg.bathroomSystemType) rows.push({ label: "System", value: cfg.bathroomSystemType });
    const materials = cfg.bathroomMaterials?.length ? cfg.bathroomMaterials.join(", ") : cfg.primaryMaterial;
    if (materials) rows.push({ label: "Material", value: materials });
    if (cfg.bathroomScopes?.length) rows.push({ label: "Scope", value: cfg.bathroomScopes.join(", ") });
    if (cfg.bathroomBaseSize) {
      const dims = cfg.baseWidth && cfg.baseLength ? `${cfg.baseWidth}" × ${cfg.baseLength}"` : cfg.bathroomBaseSize;
      rows.push({ label: "Base", value: dims });
    }
    if (cfg.baseType) rows.push({ label: "Base type", value: cfg.baseType });
    if (cfg.selectedWallSystem) rows.push({ label: "Wall system", value: cfg.selectedWallSystem });
    if (cfg.hardwareFinish) rows.push({ label: "Hardware", value: cfg.hardwareFinish });
    if (cfg.bathroomPhase2Addons?.length) rows.push({ label: "Add-ons", value: cfg.bathroomPhase2Addons.join(", ") });
    return rows;
  }, [opportunity]);


  if (!result) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          Appointment result not found.
        </CardContent>
      </Card>
    );
  }

  const resultCfg = RESULT_TYPE_CONFIG[result.resultType];
  const ResultIcon = RESULT_ICON[result.resultType];

  // ── Section 1: Outcome Summary ─────────────────────────────────────
  const SummarySection = (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${resultCfg.bg}`}>
              <ResultIcon className={`h-5 w-5 ${resultCfg.color}`} />
            </div>
            <div>
              <CardTitle className="text-lg leading-tight">{result.customerName}</CardTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                {result.buildSummary || "Appointment outcome"}
              </p>
            </div>
          </div>
          <Badge variant="outline" className={`${resultCfg.color} border-current`}>
            {resultCfg.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="flex items-start gap-2">
            <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Rep</p>
              <p className="truncate">{resolveProfileDisplayName(result.assignedRep, undefined, "Unassigned")}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Appointment</p>
              <p className="truncate">{formatDateTime(result.appointmentDatetime)}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Source</p>
              <p className="truncate">{result.source}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Tag className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Opportunity</p>
              <Link to={`/opportunities/${result.opportunityId}`} className="truncate text-primary hover:underline block">
                {result.opportunityId}
              </Link>
            </div>
          </div>
        </div>

        {/* Lifecycle + comms status badges */}
        {ext && (
          <div className="flex flex-wrap gap-2 mt-4">
            {(() => {
              const tone = lifecycleToneClasses(ext.lifecycle);
              const comms = commsStatusToneClasses(ext.lifecycle.communicationStatus);
              return (
                <>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border ${tone.container} ${tone.text}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                    {tone.label}
                    {ext.lifecycle.closeReason && ext.lifecycle.closeReason !== "won" && (
                      <span className="opacity-70">· {CLOSE_REASON_LABELS[ext.lifecycle.closeReason]}</span>
                    )}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border ${comms.container} ${comms.text}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${comms.dot}`} />
                    Comms · {COMMUNICATION_STATUS_LABELS[ext.lifecycle.communicationStatus]}
                  </span>
                </>
              );
            })()}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // ── Section 1.5: Actual Build (what the rep configured) ──────────
  // Sourced from opportunity.projectConfig — distinct from Proposal Snapshot.
  const BuildSection = (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wrench className="h-4 w-4 text-muted-foreground" />
          Actual build presented
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Configured by the rep in Step 6 · what the homeowner actually saw
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {buildRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {opportunity ? "No build was configured for this appointment." : "Linked opportunity not found."}
            {opportunity && (
              <>
                {" "}
                <Link to={`/opportunities/${opportunity.id}`} className="text-primary hover:underline">
                  Open opportunity →
                </Link>
              </>
            )}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {buildRows.map((r) => (
                <div key={r.label}>
                  <p className="text-xs text-muted-foreground">{r.label}</p>
                  <p className="truncate">{r.value}</p>
                </div>
              ))}
            </div>
            {opportunity && (
              <>
                <Separator className="my-3" />
                <Link
                  to={`/opportunities/${opportunity.id}`}
                  className="text-xs text-primary hover:underline"
                >
                  Open full build configuration →
                </Link>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  // ── Section 2: Proposal Snapshot ──────────────────────────────────
  const ProposalSection = ext?.proposal ? (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          Proposal snapshot
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Captured at submission · immutable
        </p>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Package</p>
            <p>{ext.proposal.package}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Layout</p>
            <p className="truncate">{ext.proposal.layout}</p>
          </div>
        </div>
        <Separator />
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Base price</span>
            <span>{formatCurrency(ext.proposal.basePrice)}</span>
          </div>
          {ext.proposal.addOns.map((a) => (
            <div key={a.label} className="flex justify-between text-muted-foreground">
              <span>+ {a.label}</span>
              <span>{formatCurrency(a.amount)}</span>
            </div>
          ))}
          {ext.proposal.discountAmount > 0 && (
            <div className="flex justify-between text-warning">
              <span>− {ext.proposal.discountReason || "Discount"}</span>
              <span>−{formatCurrency(ext.proposal.discountAmount)}</span>
            </div>
          )}
          <Separator className="my-2" />
          <div className="flex justify-between font-medium">
            <span>Final price presented</span>
            <span>{formatCurrency(ext.proposal.finalPrice)}</span>
          </div>
        </div>
        <Separator />
        <div className="flex items-center gap-2 text-sm">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <span className="capitalize">{ext.proposal.paymentStructure}</span>
          {ext.proposal.financingTerms && (
            <span className="text-muted-foreground">· {ext.proposal.financingTerms}</span>
          )}
        </div>
      </CardContent>
    </Card>
  ) : null;

  // ── Section 3: Objections ─────────────────────────────────────────
  const ObjectionsSection = ext?.objections ? (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          Objections
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-warning/40 text-warning">
            Primary
          </Badge>
          <span>{OBJECTION_LABELS[ext.objections.primary]}</span>
        </div>
        {ext.objections.secondary && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-muted text-muted-foreground">
              Secondary
            </Badge>
            <span className="text-muted-foreground">{OBJECTION_LABELS[ext.objections.secondary]}</span>
          </div>
        )}
        {ext.objections.notes && (
          <p className="text-muted-foreground pt-1 border-t border-border">
            {ext.objections.notes}
          </p>
        )}
      </CardContent>
    </Card>
  ) : null;

  // ── Section 4: Next Steps ─────────────────────────────────────────
  const NextStepSection = ext ? (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-muted-foreground" />
          Next steps
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Follow-up</span>
          <span>
            {ext.nextStep.followUpRequired
              ? FOLLOW_UP_TYPE_LABELS[ext.nextStep.followUpType]
              : "None required"}
          </span>
        </div>
        {ext.nextStep.followUpDueAt && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Due</span>
            <span>{formatDateTime(ext.nextStep.followUpDueAt)}</span>
          </div>
        )}
        {ext.nextStep.managerReviewRequired && (
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="outline" className="border-warning/40 text-warning">
              Manager review required
            </Badge>
          </div>
        )}
        {ext.nextStep.discountApprovalFlag && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-warning/40 text-warning">
              Discount approval flagged
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  ) : null;

  // ── Section 5: Activity ───────────────────────────────────────────
  const ActivitySection = (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          Activity
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2 text-sm">
        <div className="flex justify-between text-muted-foreground">
          <span>Result captured</span>
          <span>{formatRelative(result.submittedAt)} · {result.submittedBy}</span>
        </div>
        {ext && (
          <div className="flex justify-between text-muted-foreground">
            <span>Outcome enriched</span>
            <span>{formatRelative(ext.capturedAt)} · {ext.capturedBy}</span>
          </div>
        )}
        {linkedFollowUps.length > 0 && (
          <>
            <Separator className="my-2" />
            <p className="text-xs text-muted-foreground">Linked follow-ups</p>
            {linkedFollowUps.slice(0, 3).map((t) => {
              const label = t.dispositionReason
                || (t.executionMethod ? `Follow-up · ${t.executionMethod.replace("_", " + ")}` : "Follow-up");
              return (
                <div key={t.id} className="flex justify-between">
                  <span className="truncate pr-2">{label}</span>
                  <span className="text-muted-foreground">{t.status}</span>
                </div>
              );
            })}
          </>
        )}
        {result.notes && (
          <>
            <Separator className="my-2" />
            <p className="text-xs text-muted-foreground">Rep notes</p>
            <p className="text-muted-foreground">{result.notes}</p>
          </>
        )}
      </CardContent>
    </Card>
  );

  if (compact) {
    return (
      <div className="space-y-3">
        {SummarySection}
        {NextStepSection}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {SummarySection}
      {BuildSection}
      {ProposalSection}
      {ObjectionsSection}
      {NextStepSection}
      {ActivitySection}
      {!ext && (
        <Card className="border-dashed">
          <CardContent className="p-4 text-sm text-muted-foreground">
            Structured outcome details have not been captured for this appointment yet.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
